import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import {
  getStepIndex,
  getStepTo,
  PREPARATION_STEPS,
  type PreparationBase,
  type PreparationParams,
} from "#/modules/les.planning/preparation-steps";
import {
  getPreparationActiviteitenData,
  getPreparationKlassen,
  getPreparationSociaalData,
} from "#/modules/les.planning/preparation.functions";
import { jaarplanningQueryOptions } from "#/modules/les.planning/query.planning";
import {
  FALLBACK_AANTAL_GROEPEN,
  getStartLessonFiltersFromLesEditData,
  startLessonFlowSearchSchema,
  type StartLessonFilters,
} from "#/modules/les.start-flow/filters";
import {
  getLesEditDataForFlow,
  getLesFlowStep3Data,
} from "#/modules/les.start-flow/les-flow.functions";

// The route options of the les preparation, shared by every page it opens over.

const CACHE = { staleTime: 5 * 60_000, preloadStaleTime: 5 * 60_000 };
// The school's activiteiten and thema's change under the docent (a colleague fixes a beweegthema):
// every visit shows what it had and refetches behind it.
const CATALOGUS = { staleTime: 0, preloadStaleTime: 5 * 60_000 };

function newSeed() {
  return Math.random().toString(36).substring(2, 15);
}

export const preparationLayoutOptions = {
  validateSearch: startLessonFlowSearchSchema,
  loader: () => getPreparationKlassen(),
  staticData: { overlay: true },
  ...CACHE,
};

/** Fills in what the URL of an opened les or a picked klas still misses. */
export async function preparationBeforeLoad(
  base: PreparationBase,
  {
    search,
    params,
    location,
    context,
  }: {
    search: StartLessonFilters;
    params: PreparationParams;
    location: { pathname: string };
    context: { user?: { defaultAantalGroepen?: number | null } | null };
  },
) {
  const to = getStepTo(base, PREPARATION_STEPS[getStepIndex(location.pathname)]!.path);
  if (search.klasIds.length > 0 && (!search.seed || search.aantalGroepen === undefined)) {
    throw redirect({
      to,
      params,
      search: {
        ...search,
        seed: search.seed ?? newSeed(),
        aantalGroepen:
          search.aantalGroepen ?? context.user?.defaultAantalGroepen ?? FALLBACK_AANTAL_GROEPEN,
      },
      replace: true,
    });
  }
  // A les that is opened arrives with only its id. Once its data is in the URL an empty klas
  // list is the docent unchecking the last klas, not a les that still has to be loaded.
  if (search.editLesId != null && search.klasIds.length === 0 && search.scheduledDate == null) {
    const lesEditData = await getLesEditDataForFlow({ data: { lesId: search.editLesId } });
    throw redirect({
      to,
      params,
      search: {
        ...search,
        ...getStartLessonFiltersFromLesEditData(lesEditData, search.editLesId),
      },
      replace: true,
    });
  }
}

export const classStepOptions = { loader: () => getPreparationKlassen(), ...CACHE };

const activitiesDeps = ({ search }: { search: StartLessonFilters }) => ({
  search: search.beweegactiviteitenreeksSearchTerm ?? "",
  includeDefaults: search.includeDefaultsBeweeg,
});

export const activitiesStepOptions = {
  loaderDeps: activitiesDeps,
  loader: ({
    context,
    deps,
    params: { school, schoolyear },
  }: {
    context: { queryClient: QueryClient };
    deps: ReturnType<typeof activitiesDeps>;
    params: PreparationParams;
  }) => {
    void context.queryClient.prefetchQuery(jaarplanningQueryOptions({ school, schoolyear }));
    return getPreparationActiviteitenData({ data: deps });
  },
  ...CATALOGUS,
};

const socialEmotionalDeps = ({ search }: { search: StartLessonFilters }) => ({
  search: search.sociaalEmotioneelThemasReeksSearchTerm ?? "",
  includeDefaults: search.includeDefaultsSociaal,
});

export const socialEmotionalStepOptions = {
  loaderDeps: socialEmotionalDeps,
  loader: ({ deps }: { deps: ReturnType<typeof socialEmotionalDeps> }) =>
    getPreparationSociaalData({ data: deps }),
  ...CATALOGUS,
};

const groupsDeps = ({ search }: { search: StartLessonFilters }) => ({
  klasIds: search.klasIds,
  aantalGroepen: search.aantalGroepen ?? FALLBACK_AANTAL_GROEPEN,
  groepeerType: search.groepeerType,
  beweegthema: search.beweegthema,
  seed: search.seed,
  lockedLeerlingen: search.lockedLeerlingen,
  excludedLeerlingen: search.excludedLeerlingen,
  beweegactiviteiten: search.beweegactiviteiten,
});

export const groupsStepOptions = {
  loaderDeps: groupsDeps,
  loader: ({ deps }: { deps: ReturnType<typeof groupsDeps> }) =>
    getLesFlowStep3Data({ data: deps }),
  ...CACHE,
};
